import { type DocumentUploadResponse, enhancedApi } from '@store/generated/forms';

/**
 * Feature-specific tag types for cache management
 */
const FORMS_V2_TAG_TYPES = ['FormV2', 'FormV2List', 'FormDraft'] as const;

/**
 * Forms V2 API - extends the generated OpenAPI client
 * This ensures we're using generated types and endpoints
 * while maintaining feature-specific customization
 */
const formsV2Api = enhancedApi
  .enhanceEndpoints({
    addTagTypes: FORMS_V2_TAG_TYPES,
    endpoints: {
      // Enhance generated endpoints with cache tags and optimistic updates
      getV2ProviderForms: {
        providesTags: result =>
          result
            ? [
                ...result.items.map(({ form }) => ({
                  type: 'FormV2' as const,
                  id: form.id || 'unknown',
                })),
                { type: 'FormV2List' as const, id: 'LIST' },
              ]
            : [{ type: 'FormV2List', id: 'LIST' }],
      },

      postV2ProviderForms: {
        invalidatesTags: [{ type: 'FormV2List', id: 'LIST' }],
      },

      postV2ProviderFormsByFormIdClone: {
        invalidatesTags: [{ type: 'FormV2List', id: 'LIST' }],
      },

      putV2ProviderFormsByFormIdDraft: {
        invalidatesTags: (_result, _error, { formId }) => [
          { type: 'FormDraft', id: formId },
          { type: 'FormV2', id: formId },
        ],
      },

      postV2ProviderFormsByFormIdPublish: {
        invalidatesTags: (_result, _error, { formId }) => [
          // Don't invalidate FormDraft to prevent refetching draft after publish
          // since the form builder navigates away after publishing
          { type: 'FormV2', id: formId },
          { type: 'FormV2List', id: 'LIST' },
        ],
      },

      getV2ProviderFormsByFormId: {
        providesTags: (_result, _error, { formId }) => [{ type: 'FormV2' as const, id: formId }],
      },

      getV2ProviderFormsByFormIdDraft: {
        providesTags: (_result, _error, { formId }) => [
          { type: 'FormDraft' as const, id: formId },
          { type: 'FormV2' as const, id: formId },
        ],
      },

      deleteV2ProviderFormsByFormId: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        invalidatesTags: (_result: any, _error: any, arg: { formId: string }) => [
          { type: 'FormV2' as const, id: arg.formId },
          { type: 'FormV2List' as const, id: 'LIST' },
        ],
      },

      deleteV2ProviderFormsByFormIdDraft: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        invalidatesTags: (_result: any, _error: any, arg: { formId: string }) => [
          { type: 'FormDraft' as const, id: arg.formId },
          { type: 'FormV2' as const, id: arg.formId },
        ],
      },
    },
  })
  .injectEndpoints({
    endpoints: builder => ({
      // Override the file upload endpoint to properly handle file uploads
      postV2ProviderFormsFormBuilderUpload: builder.mutation<DocumentUploadResponse, File>({
        query: file => {
          const formData = new FormData();
          formData.append('file', file, file.name);

          return {
            url: `/v2/provider/forms/form-builder/upload`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: ['FormV2List'], // Refresh form list after successful generation
      }),
    }),
    overrideExisting: true,
  });

// Export the API for cache invalidation
export { formsV2Api };

// Export hooks with user-friendly names
export const {
  useGetV2ProviderFormsQuery: useGetFormsQuery,
  usePostV2ProviderFormsMutation: useCreateFormMutation,
  usePostV2ProviderFormsByFormIdCloneMutation: useCloneFormMutation,
  usePutV2ProviderFormsByFormIdDraftMutation: useSaveDraftMutation,
  usePostV2ProviderFormsByFormIdPublishMutation: usePublishDraftMutation,
  useGetV2ProviderFormsByFormIdQuery: useGetFormByIdQuery,
  useGetV2ProviderFormsByFormIdDraftQuery: useGetFormDraftQuery,
  useDeleteV2ProviderFormsByFormIdMutation: useDeleteFormMutation,
  useDeleteV2ProviderFormsByFormIdDraftMutation: useDeleteFormDraftMutation,
  // Note: File upload now uses direct fetch in useFormBuilderFileUpload hook
  useGetV2ProviderFormsFormBuilderWorkflowByWorkflowTaskIdQuery: useFormBuilderWorkflowQuery,
  useLazyGetV2ProviderFormsFormBuilderWorkflowByWorkflowTaskIdQuery: useLazyFormBuilderWorkflowQuery,
  usePostV2ProviderFormsFormBuilderGenerateFromTextMutation: useFormBuilderTextGenerationMutation,
} = formsV2Api;
